import type { Tab } from "@/types/tabbar";
import { CloseButton, Flex, Text } from "@mantine/core";
import { Link, useLocation } from "react-router";
import "./Tabbar.css";

export function Tabbar({
  tabs,
  onClose,
  actionButton,
  activeTabMatcher,
}: {
  tabs: Tab[];
  onClose?: (tabId: string) => void;
  actionButton?: React.ReactNode;
  activeTabMatcher?: (pathname: string, tab: Tab) => boolean;
}) {
  const { pathname } = useLocation();

  const defaultActiveMatcher = (pathname: string, tab: Tab): boolean => {
    return pathname === tab.to || pathname.startsWith(tab.to + "/");
  };

  const isTabActive = (tab: Tab): boolean => {
    if (activeTabMatcher) {
      return activeTabMatcher(pathname, tab);
    }
    return defaultActiveMatcher(pathname, tab);
  };

  return (
    <header className="tabbar">
      {tabs.map((tab) => {
        const IconComponent = tab.icon;
        const isActive = isTabActive(tab);
        const isClosable = tab.isClosable ?? false;

        const tabContent = (
          <Flex
            align="center"
            gap="xs"
            px="md"
            bg={isActive ? "white" : "transparent"}
            className={`tabbar-link ${isActive ? "tabbar-link-active" : ""}`}
            style={{
              borderColor: isActive
                ? "var(--mantine-color-border-2)"
                : "transparent",
            }}
          >
            <Link to={tab.to} style={{ display: "flex", minWidth: 0, flex: 1 }}>
              <Flex
                align="center"
                gap={8}
                style={{ minWidth: 0, overflow: "hidden" }}
              >
                <IconComponent
                  size={16}
                  color={
                    isActive
                      ? "var(--mantine-color-eu_flag-filled)"
                      : "var(--mantine-color-gray-9)"
                  }
                />
                <Text
                  c={isActive ? "dark.9" : "gray.9"}
                  fz={14}
                  fw={400}
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    minWidth: 0,
                  }}
                >
                  {tab.label}
                </Text>
              </Flex>
            </Link>
            {onClose && tab.isClosable && (
              <CloseButton
                className="tabbar-close-button"
                size="sm"
                color="gray.9"
                c="gray.9"
                onClick={() => onClose(tab.id)}
              />
            )}
          </Flex>
        );

        // Wrap closable tabs
        if (isClosable) {
          return (
            <div key={tab.id} className="tabbar-link-wrapper">
              {tabContent}
            </div>
          );
        }

        // Non-closable tabs don't need wrapper
        return (
          <div
            key={tab.id}
            style={{
              flexShrink: 0,
              display: "flex",
              alignItems: "flex-end",
              height: "100%",
            }}
          >
            {tabContent}
          </div>
        );
      })}
      {actionButton}
    </header>
  );
}
